/**
 * Composable to detect user device type (Desktop or Mobile)
 * Returns 'D' for desktop, 'M' for mobile
 * Works on both server (using user-agent) and client (using viewport width)
 */
export const useDevice = () => {
  const headers = useRequestHeaders(['user-agent'])

  // Use a function to get fresh device value on each access (not cached)
  const getDevice = (): 'D' | 'M' => {
    if (import.meta.server) {
      const ua = headers['user-agent'] || ''
      return /Mobile|Android|iPhone|iPad|iPod/i.test(ua) ? 'M' : 'D'
    }
    return window.innerWidth < 1024 ? 'M' : 'D'
  }

  // Reactive device value that updates on window resize
  const device = ref<'D' | 'M'>(getDevice())

  if (import.meta.client) {
    onMounted(() => {
      // Update device value based on current viewport
      device.value = getDevice()

      // Optional: Listen for resize events to update device value
      const handleResize = () => {
        device.value = getDevice()
      }
      window.addEventListener('resize', handleResize)
      onUnmounted(() => {
        window.removeEventListener('resize', handleResize)
      })
    })
  }

  const isMobile = computed(() => device.value === 'M')
  const isDesktop = computed(() => device.value === 'D')

  return {
    device,
    isMobile,
    isDesktop
  }
}
